import React, { useEffect } from 'react';
import { TeamlessCollaborationPromptPopover } from './TeamlessCollaborationPromptPopover';
import { useTeamlessCollaborationPromptQuery } from '@trello/graphql/generated';
import { TeamlessCollaborationPromptPopoverId } from 'app/gamma/src/components/popovers/popover-ids';
import { PopoverProps, usePopover } from 'app/gamma/src/components/popover';
import { Noun, Verb, trackUe } from '@trello/analytics';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { Team, Feature } from 'app/scripts/debug/constants';

interface TeamlessCollaborationPromptProps {
  boardId: string;
  onClose: () => void;
  onTeamCreated: () => void;
  target: PopoverProps['target'];
}

export const TeamlessCollaborationPrompt: React.FunctionComponent<TeamlessCollaborationPromptProps> = ({
  boardId,
  onClose,
  onTeamCreated,
  target,
}) => {
  const { isPopoverShown, showPopover, closePopover } = usePopover({
    name: TeamlessCollaborationPromptPopoverId,
  });

  const { data } = useTeamlessCollaborationPromptQuery({
    variables: { boardId },
  });

  /**
   * For the prompt to be shown, the board and user must meet the following:
   * - The board does not belong to a team
   * - The board has at least 3 confirmed members
   * - The user is a board admin
   * - The user is not a member of an enterprise team
   * If the user is an enterprise team member, the Private Garden rules would
   * prevent the goal of this feature- creating a free team.
   */
  const meId = data?.member?.id;
  const meMembership = data?.board?.memberships?.find(
    (membership) => membership.idMember === meId,
  );
  const isBoardAdmin = meMembership?.memberType === 'admin';
  const isEnterpriseMember = !!data?.member?.enterprises?.length;
  const isTeamless = !data?.board?.idOrganization;
  const numberOfConfirmedCollaborators = data?.board?.members?.reduce(
    (count, member) => {
      if (member.confirmed) {
        count = count + 1;
      }
      return count;
    },
    0,
  );
  const hasEnoughCollaborators =
    numberOfConfirmedCollaborators && numberOfConfirmedCollaborators >= 3;
  const boardAndMemberMeetRequirements =
    isBoardAdmin && isTeamless && hasEnoughCollaborators && !isEnterpriseMember;

  useEffect(() => {
    if (isPopoverShown && !isTeamless) {
      closePopover();
      Alerts.show('teamless-board-added-to-team', 'info', 'board', 5000);
    } else if (boardAndMemberMeetRequirements && !isPopoverShown) {
      showPopover();
      trackUe({
        category: Noun.BOARD_VIEW,
        verb: Verb.VIEWS,
        directObj: Noun.TEAMLESS_COLLABORATION_PROMPT,
        context: {
          boardId,
        },
      });
    }
  });

  return isPopoverShown ? (
    <ErrorBoundary
      tags={{
        ownershipArea: Team.BizTeam,
        feature: Feature.TeamlessCollaborationPrompt,
      }}
    >
      <TeamlessCollaborationPromptPopover
        boardId={boardId}
        onClose={onClose}
        onTeamCreated={onTeamCreated}
        target={target}
      />
    </ErrorBoundary>
  ) : null;
};
